import { useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtDate, fmtDateTime, formatApiError, openAuthorizedDocument } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StagePill, { LOAN_STAGES } from "@/components/StagePill";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, FileText, Trash2, Download, Check, X, Send, Archive, ArchiveRestore, Pencil, Eye, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import DocumentPreviewModal from "@/components/DocumentPreviewModal";
import MeetingsPanel from "@/components/MeetingsPanel";
import TextBorrowerModal from "@/components/TextBorrowerModal";
import NotesPanel from "@/components/NotesPanel";

const FOLDERS = ["Financials","Tax Returns","Bank Statements","Corporate Documents","Collateral","Real Estate","Legal","Insurance","Other"];
const DOC_STATUS = {
  pending: { label: "Pending", cls: "text-[#1F2937]" },
  accepted: { label: "Accepted", cls: "text-[#1F2937]" },
  rejected: { label: "Rejected", cls: "text-[#1F2937]" },
  needs_revision: { label: "Needs Revision", cls: "text-[#1F2937]" },
};
const inputCls = "border-0 shadow-none rounded-none px-0 h-9 bg-transparent font-semibold text-[16px] focus-visible:ring-0 focus-visible:ring-offset-0";

/**
 * Turn something like:
 *   "424bf91f-2188-449f-a57b-6e3383f3b86a/13539/20260714055638_20260331-statements-5843_watermarked.pdf"
 * into:
 *   "20260331-statements-5843.pdf"
 * — strip any folder prefix, leading timestamp, and trailing "_watermarked".
 */
function prettyFilename(name) {
  if (!name) return "";
  // Strip any folder path (SwitchBox sends UUID/13539/<file>)
  let base = name.split(/[\\/]/).pop();
  // Strip a leading 12-16 digit timestamp then underscore (e.g. 20260714055638_)
  base = base.replace(/^\d{12,17}_/, "");
  // Strip a "_watermarked" / "-watermarked" suffix before the extension
  base = base.replace(/[_-]watermarked(?=\.[^.]+$)/i, "");
  return base;
}

function DocStatusBadge({ status, onChange, canEdit }) {
  const s = DOC_STATUS[status] || DOC_STATUS.pending;
  if (!canEdit) return <span className={`small-caps ${s.cls}`}>{s.label}</span>;
  return (
    <Select value={status} onValueChange={onChange}>
      <SelectTrigger className={`h-7 w-40 border-0 shadow-none rounded-none bg-transparent focus:ring-0 small-caps ${s.cls}`}><SelectValue /></SelectTrigger>
      <SelectContent>{Object.entries(DOC_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function DocUpload({ appId, onUploaded, requiredDocs, canRequestSignature }) {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [folder, setFolder] = useState("Financials");
  const [docType, setDocType] = useState("");
  const [requiresSig, setRequiresSig] = useState(false);
  const fileRef = useRef();

  const doUpload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", folder);
      fd.append("document_type", docType || file.name);
      if (canRequestSignature) fd.append("requires_signature", requiresSig ? "true" : "false");
      await api.post(`/applications/${appId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded${requiresSig ? " · signature requested" : ""}`);
      onUploaded();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    Array.from(e.dataTransfer.files).forEach(doUpload);
  };

  return (
    <div className="hair-y py-8">
      <div className="flex items-center justify-between flex-wrap gap-6">
        <div className="small-caps text-slate-500">Upload documents</div>
        <div className="flex items-center gap-8 flex-wrap">
          <div>
            <div className="small-caps text-slate-400 mb-1">Folder</div>
            <Select value={folder} onValueChange={setFolder}>
              <SelectTrigger className="w-44 h-8 border-0 shadow-none rounded-none px-0 bg-transparent focus:ring-0 font-semibold text-[15px]" data-testid="doc-folder-select"><SelectValue /></SelectTrigger>
              <SelectContent>{FOLDERS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <div className="small-caps text-slate-400 mb-1">Document Type</div>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger className="w-64 h-8 border-0 shadow-none rounded-none px-0 bg-transparent focus:ring-0 font-semibold text-[15px]" data-testid="doc-type-select"><SelectValue placeholder="Auto-detect" /></SelectTrigger>
              <SelectContent>
                {(requiredDocs || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {canRequestSignature && (
            <label className="inline-flex items-center gap-2 small-caps text-slate-600 cursor-pointer select-none" data-testid="doc-requires-signature-label">
              <input
                type="checkbox"
                checked={requiresSig}
                onChange={(e) => setRequiresSig(e.target.checked)}
                data-testid="doc-requires-signature"
                className="h-4 w-4"
              />
              Require borrower signature
            </label>
          )}
        </div>
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        data-testid="doc-drop-zone"
        className={`mt-6 cursor-pointer border border-dashed p-12 text-center transition-colors ${
          drag ? "border-[#1F2937] bg-slate-800/[0.03]" : "border-slate-300 hover:border-[#1F2937]/45"
        }`}
      >
        <Upload className="mx-auto h-6 w-6 text-slate-400" />
        <div className="mt-3 font-semibold text-[19px] text-[#1F2937]">
          {busy ? "Uploading…" : "Drag files here or click to browse"}
        </div>
        <div className="mt-2 small-caps text-slate-400">
          {requiresSig && canRequestSignature
            ? "Uploaded files will be sent to the borrower for e-signature"
            : "PDF · Excel · CSV · Word · Images · ZIP"}
        </div>
        <input ref={fileRef} type="file" hidden multiple onChange={(e) => Array.from(e.target.files || []).forEach(doUpload)} data-testid="doc-file-input" />
      </div>
    </div>
  );
}

function RequiredDocsChecklist({ app, docs, canEdit, onSaved }) {
  const [adding, setAdding] = useState(false);
  const [newDoc, setNewDoc] = useState("");
  const [saving, setSaving] = useState(false);

  const required = app?.required_documents || [];
  const manuallyChecked = app?.required_documents_checked || [];

  const hasMatchingUpload = (label) => {
    if (!label) return false;
    const target = label.toLowerCase();
    return docs.some((d) => {
      const type = (d.document_type || "").toLowerCase();
      const name = (d.filename || "").toLowerCase();
      return type === target || type.includes(target) || target.includes(type) || name.includes(target);
    });
  };

  const isChecked = (label) =>
    manuallyChecked.includes(label) || hasMatchingUpload(label);

  const persist = async (patch) => {
    setSaving(true);
    try {
      await api.patch(`/applications/${app.id}`, patch);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleChecked = async (label) => {
    if (!canEdit) return;
    const already = manuallyChecked.includes(label);
    const next = already
      ? manuallyChecked.filter((x) => x !== label)
      : [...manuallyChecked, label];
    await persist({ required_documents_checked: next });
  };

  const addItem = async () => {
    const val = newDoc.trim();
    if (!val) return;
    if (required.some((r) => r.toLowerCase() === val.toLowerCase())) {
      toast.error("Already in the checklist");
      return;
    }
    await persist({ required_documents: [...required, val] });
    setNewDoc("");
    setAdding(false);
  };

  const removeItem = async (label) => {
    if (!confirm(`Remove "${label}" from the required list?`)) return;
    await persist({
      required_documents: required.filter((r) => r !== label),
      required_documents_checked: manuallyChecked.filter((r) => r !== label),
    });
  };

  const receivedCount = required.filter(isChecked).length;
  const total = required.length;

  return (
    <div className="hair-y py-8" data-testid="required-docs-checklist">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-4">
        <div className="small-caps text-slate-500">
          Required documents
          {total > 0 && (
            <span className="ml-3 text-slate-400">
              {receivedCount} of {total} received
            </span>
          )}
        </div>
        {canEdit && !adding && (
          <button
            onClick={() => setAdding(true)}
            className="group inline-flex items-center gap-3"
            data-testid="checklist-add-btn"
          >
            <span className="h-[1px] w-8 bg-slate-800 transition-all group-hover:w-12" />
            <span className="small-caps text-[#1F2937]">Add required document</span>
          </button>
        )}
      </div>
      {required.length === 0 ? (
        <div className="py-8 text-center font-semibold text-slate-400 text-[15px]">
          No required documents set for this file.
        </div>
      ) : (
        <ul className="divide-y divide-slate-200 border-y border-slate-200">
          {required.map((label) => {
            const received = isChecked(label);
            return (
              <li
                key={label}
                className="flex items-center justify-between py-3"
                data-testid={`checklist-item-${label}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <button
                    type="button"
                    disabled={!canEdit || saving}
                    onClick={() => toggleChecked(label)}
                    className={`inline-flex items-center justify-center h-5 w-5 border transition-colors ${
                      received
                        ? "border-[#1F2937] bg-[#1F2937] text-white"
                        : "border-slate-300 bg-white hover:border-[#1F2937]"
                    } ${canEdit ? "cursor-pointer" : "cursor-default"}`}
                    aria-label={received ? "Mark as not received" : "Mark as received"}
                    data-testid={`checklist-item-status-${label}`}
                  >
                    {received && <Check className="h-3 w-3" strokeWidth={3} />}
                  </button>
                  <span
                    className={`font-semibold text-[15px] ${
                      received ? "text-[#1F2937]" : "text-slate-500"
                    }`}
                  >
                    {label}
                  </span>
                  {!received && (
                    <span className="small-caps text-slate-400 text-[10px] ml-2">
                      Awaiting
                    </span>
                  )}
                </div>
                {canEdit && (
                  <button
                    onClick={() => removeItem(label)}
                    className="text-slate-400 hover:text-[#1F2937]"
                    title="Remove from list"
                    data-testid={`checklist-remove-${label}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {canEdit && adding && (
        <div className="mt-6 flex items-center gap-4">
          <Input
            autoFocus
            value={newDoc}
            onChange={(e) => setNewDoc(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") addItem();
              if (e.key === "Escape") {
                setAdding(false);
                setNewDoc("");
              }
            }}
            placeholder="e.g. YTD Profit & Loss, Property appraisal, Driver's license…"
            data-testid="checklist-new-input"
            className={`${inputCls} flex-1 border-b border-slate-300 focus-visible:border-[#1F2937]`}
          />
          <button
            onClick={addItem}
            disabled={saving || !newDoc.trim()}
            className="group inline-flex items-center gap-3 disabled:opacity-50"
            data-testid="checklist-save-btn"
          >
            <span className="h-[1px] w-8 bg-slate-800 transition-all group-hover:w-12" />
            <span className="small-caps text-[#1F2937]">Add</span>
          </button>
          <button
            onClick={() => {
              setAdding(false);
              setNewDoc("");
            }}
            className="small-caps text-slate-500 hover:text-[#1F2937]"
            data-testid="checklist-cancel-btn"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}



export default function ApplicationDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [app, setApp] = useState(null);
  const [docs, setDocs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const [internal, setInternal] = useState(false);
  const [team, setTeam] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [textOpen, setTextOpen] = useState(false);
  const canEdit = user?.role === "loan_rep" || user?.role === "admin";
  const isAdmin = user?.role === "admin";

  const load = () => Promise.all([
    api.get(`/applications/${id}`).then((r) => setApp(r.data)),
    api.get(`/applications/${id}/documents`).then((r) => setDocs(r.data)),
    api.get(`/applications/${id}/messages`).then((r) => setMessages(r.data)),
  ]);
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  useEffect(() => {
    if (isAdmin) api.get("/users/team").then((r) => setTeam(r.data)).catch(() => {});
  }, [isAdmin]);

  const assignRep = async (loan_rep_id) => {
    try {
      await api.patch(`/applications/${id}/assign`, { loan_rep_id });
      toast.success("Loan officer assigned");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [stageDraft, setStageDraft] = useState(null); // {stage, note}
  const openStageChange = (stage) => {
    if (stage === app?.stage) return;
    setStageDraft({ stage, note: "" });
  };
  const cancelStageChange = () => setStageDraft(null);
  const commitStageChange = async () => {
    if (!stageDraft) return;
    try {
      await api.patch(`/applications/${id}/stage`, { stage: stageDraft.stage, note: stageDraft.note.trim() });
      toast.success(`Moved to ${stageDraft.stage}${stageDraft.note.trim() ? " · note sent to borrower" : ""}`);
      setStageDraft(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const sendMessage = async () => {
    if (!msg.trim()) return;
    try { await api.post(`/applications/${id}/messages`, { body: msg, internal }); setMsg(""); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const setDocStatus = async (docId, status) => { try { await api.patch(`/documents/${docId}/status`, { status }); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const deleteDoc = async (docId) => { if (!confirm("Remove this document?")) return; try { await api.delete(`/documents/${docId}`); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const [signing, setSigning] = useState(null); // { doc, name }
  const submitSignature = async () => {
    if (!signing?.doc || !signing.name?.trim()) return;
    try {
      await api.post(`/documents/${signing.doc.id}/sign`, { signature_name: signing.name.trim() });
      toast.success("Document signed");
      setSigning(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Auto-open edit mode when arriving with ?edit=1
  useEffect(() => {
    if (canEdit && app && params.get("edit") === "1" && !editMode) {
      setEditDraft({
        product_name: app.product_name || "",
        purpose: app.purpose || "",
        requested_amount: app.requested_amount || "",
        industry: app.industry || "",
        time_in_business: app.time_in_business || "",
        annual_revenue: app.annual_revenue || "",
        fico: app.fico || "",
        business_legal_name: app.business_legal_name || "",
      });
      setEditMode(true);
      const next = new URLSearchParams(params); next.delete("edit");
      setParams(next, { replace: true });
    }
  }, [app, params, canEdit, editMode, setParams]);

  const startEdit = () => {
    setEditDraft({
      product_name: app.product_name || "",
      purpose: app.purpose || "",
      requested_amount: app.requested_amount || "",
      industry: app.industry || "",
      time_in_business: app.time_in_business || "",
      annual_revenue: app.annual_revenue || "",
      fico: app.fico || "",
      business_legal_name: app.business_legal_name || "",
    });
    setEditMode(true);
  };
  const cancelEdit = () => { setEditMode(false); setEditDraft(null); };
  const saveEdit = async () => {
    try {
      const payload = { ...editDraft };
      // Coerce numeric fields
      ["requested_amount", "time_in_business", "annual_revenue", "fico"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
        else payload[k] = Number(payload[k]);
      });
      // Drop empty strings for other fields — keep undefined so the API doesn't null them
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
      });
      await api.patch(`/applications/${id}`, payload);
      toast.success("File updated");
      setEditMode(false);
      setEditDraft(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const archiveFile = async () => {
    try {
      if (app.archived) {
        await api.post(`/applications/${id}/unarchive`);
        toast.success("File restored");
      } else {
        await api.post(`/applications/${id}/archive`);
        toast.success("File archived");
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteFile = async () => {
    try {
      await api.delete(`/applications/${id}`);
      toast.success("File permanently deleted");
      nav("/app/applications");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteMessage = async (m) => {
    if (!confirm("Delete this message? This cannot be undone.")) return;
    try {
      await api.delete(`/messages/${m.id}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [invite, setInvite] = useState({ busy: false, result: null });
  const inviteSubmitter = async () => {
    if (!isAdmin) return;
    setInvite({ busy: true, result: null });
    try {
      const r = await api.post(`/admin/applications/${id}/invite-submitter`, { role: "broker" });
      setInvite({ busy: false, result: r.data });
      toast.success(r.data.created ? "Invited as broker" : "Broker linked to file");
      load();
    } catch (e) {
      setInvite({ busy: false, result: null });
      toast.error(formatApiError(e));
    }
  };

  const [aiResp, setAiResp] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const runAI = async (prompt) => {
    setAiBusy(true); setAiResp("");
    try {
      const { data } = await api.post("/ai/chat", { session_id: `app-${id}`, application_id: id, message: prompt });
      setAiResp(data.response);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiBusy(false); }
  };

  if (!app) return <div className="grid place-items-center h-96 small-caps text-slate-400">Loading…</div>;

  return (
    <div className="max-w-[1200px] space-y-14" data-testid="app-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <Link to="/app/applications" className="small-caps text-slate-500 hover:text-[#1F2937]">← All files</Link>
        {canEdit && (
          <div className="flex items-center gap-6" data-testid="file-admin-controls">
            {app.archived && (
              <span className="inline-flex items-center gap-1 border border-slate-400 text-slate-600 text-[10px] uppercase tracking-wider px-2 py-1" data-testid="file-archived-badge">
                Archived
              </span>
            )}
            {!editMode && (
              <button
                onClick={startEdit}
                className="small-caps text-[#1F2937] hover:opacity-70 inline-flex items-center gap-2"
                data-testid="file-edit-btn"
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
            )}
            <button
              onClick={archiveFile}
              className="small-caps text-[#1F2937] hover:opacity-70 inline-flex items-center gap-2"
              data-testid={app.archived ? "file-unarchive-btn" : "file-archive-btn"}
            >
              {app.archived ? (
                <><ArchiveRestore className="h-3.5 w-3.5" /> Restore</>
              ) : (
                <><Archive className="h-3.5 w-3.5" /> Archive</>
              )}
            </button>
            {isAdmin && (
              <button
                onClick={() => setConfirmDelete(true)}
                className="small-caps text-red-700 hover:opacity-70 inline-flex items-center gap-2"
                data-testid="file-delete-btn"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            )}
          </div>
        )}
      </div>

      {editMode && editDraft && (
        <div className="border border-slate-800 p-6 bg-slate-800/[0.03]" data-testid="file-edit-form">
          <div className="flex items-baseline justify-between">
            <div className="small-caps text-[#1F2937]">Edit file details</div>
            <div className="small-caps text-slate-500 text-[10px]">File #{app.loan_number}</div>
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <div className="border-b border-slate-300 pb-1">
              <div className="small-caps text-slate-500 mb-1">Program name</div>
              <Input
                value={editDraft.product_name}
                onChange={(e) => setEditDraft({ ...editDraft, product_name: e.target.value })}
                data-testid="edit-product-name"
                className={inputCls}
              />
            </div>
            <div className="border-b border-slate-300 pb-1">
              <div className="small-caps text-slate-500 mb-1">Requested amount</div>
              <Input
                type="number"
                value={editDraft.requested_amount}
                onChange={(e) => setEditDraft({ ...editDraft, requested_amount: e.target.value })}
                data-testid="edit-requested-amount"
                className={inputCls}
              />
            </div>
            <div className="border-b border-slate-300 pb-1 md:col-span-2">
              <div className="small-caps text-slate-500 mb-1">Business legal name</div>
              <Input
                value={editDraft.business_legal_name}
                onChange={(e) => setEditDraft({ ...editDraft, business_legal_name: e.target.value })}
                data-testid="edit-business-legal-name"
                className={inputCls}
              />
            </div>
            <div className="border-b border-slate-300 pb-1">
              <div className="small-caps text-slate-500 mb-1">Industry</div>
              <Input
                value={editDraft.industry}
                onChange={(e) => setEditDraft({ ...editDraft, industry: e.target.value })}
                data-testid="edit-industry"
                className={inputCls}
              />
            </div>
            <div className="border-b border-slate-300 pb-1">
              <div className="small-caps text-slate-500 mb-1">Time in business (yrs)</div>
              <Input
                type="number"
                value={editDraft.time_in_business}
                onChange={(e) => setEditDraft({ ...editDraft, time_in_business: e.target.value })}
                data-testid="edit-time-in-business"
                className={inputCls}
              />
            </div>
            <div className="border-b border-slate-300 pb-1">
              <div className="small-caps text-slate-500 mb-1">Annual revenue</div>
              <Input
                type="number"
                value={editDraft.annual_revenue}
                onChange={(e) => setEditDraft({ ...editDraft, annual_revenue: e.target.value })}
                data-testid="edit-annual-revenue"
                className={inputCls}
              />
            </div>
            <div className="border-b border-slate-300 pb-1">
              <div className="small-caps text-slate-500 mb-1">FICO</div>
              <Input
                type="number"
                value={editDraft.fico}
                onChange={(e) => setEditDraft({ ...editDraft, fico: e.target.value })}
                data-testid="edit-fico"
                className={inputCls}
              />
            </div>
            <div className="border-b border-slate-300 pb-1 md:col-span-2">
              <div className="small-caps text-slate-500 mb-1">Purpose / use of funds</div>
              <Textarea
                rows={3}
                value={editDraft.purpose}
                onChange={(e) => setEditDraft({ ...editDraft, purpose: e.target.value })}
                data-testid="edit-purpose"
                className="border-0 rounded-none px-0 bg-transparent shadow-none font-semibold text-[16px] focus-visible:ring-0 focus-visible:ring-offset-0 resize-none"
              />
            </div>
          </div>
          <div className="mt-6 flex items-center justify-end gap-6">
            <button
              onClick={cancelEdit}
              className="small-caps text-slate-500 hover:text-[#1F2937]"
              data-testid="file-edit-cancel"
            >
              Cancel
            </button>
            <button
              onClick={saveEdit}
              className="small-caps text-[#1F2937] hover:opacity-70 inline-flex items-center gap-2"
              data-testid="file-edit-save"
            >
              <Check className="h-4 w-4" /> Save changes
            </button>
          </div>
        </div>
      )}

      {(() => {
        const info = app?.additional_info || {};
        const source = info.source;
        const label = source === "crm_webhook" ? (info.submitted_by_name || info.submitted_by_email || "CRM") :
                      source === "qualifier"   ? "Borrower qualifier upload" :
                      source === "referral_link" ? (info.referred_by_name || "Referral link") :
                      null;
        if (!label) return null;
        const matched = info.submitted_by_matched;
        return (
          <div
            className="border border-slate-800 bg-[#1F2937] text-white p-6 flex items-center justify-between gap-6 flex-wrap"
            data-testid="app-source-banner"
          >
            <div>
              <div className="small-caps text-white/60">Deal source</div>
              <div className="mt-1 font-semibold text-[22px] tracking-tight">{label}</div>
              {info.submitted_by_email && info.submitted_by_name && (
                <div className="mt-1 small-caps text-white/60 lowercase">{info.submitted_by_email}</div>
              )}
            </div>
            <div className="text-right space-y-1">
              <div className="small-caps text-white/60">Referral rep matched</div>
              {matched === true ? (
                <div className="font-semibold text-[13px] tracking-tight">Yes · linked as broker</div>
              ) : matched === false ? (
                isAdmin && source === "crm_webhook" && info.submitted_by_email ? (
                  <button
                    onClick={inviteSubmitter}
                    disabled={invite.busy}
                    data-testid="invite-submitter-btn"
                    className="mt-1 inline-flex items-center gap-2 border border-white/60 px-4 py-2 small-caps hover:bg-white hover:text-[#1F2937] transition-colors disabled:opacity-50"
                  >
                    {invite.busy ? "Inviting…" : "Not on personnel — invite them →"}
                  </button>
                ) : (
                  <div className="font-semibold text-[13px] tracking-tight">Not on Personnel</div>
                )
              ) : (
                <div className="font-semibold text-[13px] tracking-tight">—</div>
              )}
            </div>
          </div>
        );
      })()}

      {invite.result && (
        <div
          className="fixed inset-0 z-[60] grid place-items-center bg-black/40 p-4"
          onClick={() => setInvite({ ...invite, result: null })}
          data-testid="invite-result-modal"
        >
          <div
            className="w-full max-w-md bg-white border border-slate-300 p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="small-caps text-slate-500">Invitation</div>
            <h3 className="mt-2 font-semibold text-[22px] text-[#1F2937]">
              {invite.result.created ? "Added to Personnel" : "Linked to file"}
            </h3>
            <p className="mt-2 text-[14px] text-slate-600 leading-relaxed">
              {invite.result.email} is now this file&apos;s broker.
              {invite.result.created && invite.result.email_sent && " We emailed them a sign-in link and temporary password."}
              {invite.result.created && !invite.result.email_sent && " We couldn't send the invitation email — share the temporary password below with them directly."}
            </p>
            {invite.result.created && invite.result.temp_password && (
              <div className="mt-6 border border-slate-300 p-4 bg-slate-50">
                <div className="small-caps text-slate-500 text-[10px]">Temporary password</div>
                <div className="mt-1 flex items-center justify-between gap-3">
                  <code className="font-mono text-[15px] text-[#1F2937] break-all">{invite.result.temp_password}</code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(invite.result.temp_password); toast.success("Copied"); }}
                    className="small-caps text-[#1F2937] hover:opacity-70"
                    data-testid="invite-copy-password"
                  >
                    Copy
                  </button>
                </div>
                <div className="mt-2 small-caps text-slate-400 text-[10px]">They must change it on first login.</div>
              </div>
            )}
            <div className="mt-8 flex items-center justify-end">
              <button
                onClick={() => setInvite({ ...invite, result: null })}
                className="small-caps text-[#1F2937] hover:opacity-70"
                data-testid="invite-close"
              >
                Done →
              </button>
            </div>
          </div>
        </div>
      )}

      {(() => {
        const notes = (app.timeline || []).filter((t) => (t.note || "").trim());
        const latest = notes[notes.length - 1];
        if (!latest) return null;
        return (
          <div
            className="border border-slate-800 bg-slate-800/[0.03] p-6"
            data-testid="latest-stage-note"
          >
            <div className="flex items-baseline justify-between gap-6 flex-wrap">
              <div className="small-caps text-[#1F2937]">
                Latest update · {latest.stage}
              </div>
              <div className="small-caps text-slate-500 text-[10px]">{fmtDateTime(latest.at)}</div>
            </div>
            <p className="mt-3 text-[16px] leading-[1.7] text-[#1F2937] whitespace-pre-wrap">
              {latest.note}
            </p>
          </div>
        );
      })()}

      <header className="grid grid-cols-12 gap-10 border-b border-slate-200 pb-10">
        <div className="col-span-12 lg:col-span-8">
          <div className="flex items-center gap-4">
            <span className="font-mono text-[12px] text-slate-500">{app.loan_number}</span>
            <StagePill stage={app.stage} />
          </div>
          <h1 className="mt-4 font-semibold text-[64px] leading-[0.95] tracking-tight text-[#1F2937]">{app.product_name}</h1>
          <p className="mt-4 max-w-2xl text-[15.5px] leading-[1.65] text-slate-600">{app.purpose}</p>
          <div className="mt-8 grid grid-cols-4 gap-8 max-w-2xl">
            <div>
              <div className="small-caps text-slate-500">Annual Revenue</div>
              <div className="mt-2 font-semibold text-[22px] text-[#1F2937]" data-testid="tile-annual-revenue">
                {app.annual_revenue ? fmtMoney(app.annual_revenue) : "—"}
              </div>
            </div>
            <div>
              <div className="small-caps text-slate-500">Credit Score</div>
              <div className="mt-2 font-semibold text-[22px] text-[#1F2937]" data-testid="tile-credit-score">
                {app.fico ?? "—"}
              </div>
            </div>
            <div>
              <div className="small-caps text-slate-500">Business Owner</div>
              <div className="mt-2 font-semibold text-[17px] text-[#1F2937]">{app.borrower?.business_name || app.borrower?.name || "—"}</div>
            </div>
            <div>
              <div className="small-caps text-slate-500">Sector</div>
              <div className="mt-2 font-semibold text-[17px] text-[#1F2937]" data-testid="tile-sector">{app.industry || "—"}</div>
            </div>
          </div>

          {user?.role !== "borrower" && (
            <div className="mt-8 hair-y py-6 grid grid-cols-3 gap-8 max-w-2xl" data-testid="borrower-contact">
              <div>
                <div className="small-caps text-slate-500">Contact</div>
                <div className="mt-2 font-semibold text-[17px] text-[#1F2937]" data-testid="borrower-contact-name">
                  {app.borrower?.name || "—"}
                </div>
                {app.borrower?.title && (
                  <div className="small-caps text-slate-400 mt-1">{app.borrower.title}</div>
                )}
              </div>
              <div>
                <div className="small-caps text-slate-500">Email</div>
                {app.borrower?.email ? (
                  <a
                    href={`mailto:${app.borrower.email}`}
                    className="mt-2 block font-semibold text-[15px] text-[#1F2937] hover:underline break-all"
                    data-testid="borrower-contact-email"
                  >
                    {app.borrower.email}
                  </a>
                ) : (
                  <div className="mt-2 font-semibold text-[15px] text-slate-400">—</div>
                )}
              </div>
              <div>
                <div className="small-caps text-slate-500">Phone</div>
                <div className="mt-2 flex items-baseline gap-3 flex-wrap">
                  {app.borrower?.phone ? (
                    <a
                      href={`tel:${app.borrower.phone}`}
                      className="block font-semibold text-[15px] text-[#1F2937] hover:underline"
                      data-testid="borrower-contact-phone"
                    >
                      {app.borrower.phone}
                    </a>
                  ) : (
                    <div className="font-semibold text-[15px] text-slate-400">Not provided</div>
                  )}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => setTextOpen(true)}
                      data-testid="text-borrower-btn"
                      className="inline-flex items-center gap-1 small-caps text-[#1F2937] hover:opacity-70 border-b border-[#1F2937]"
                      title="Text this borrower an upload link"
                    >
                      <MessageSquare className="h-3 w-3" /> Text upload link
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="col-span-12 lg:col-span-4 lg:pl-8 lg:border-l lg:border-slate-200">
          <div className="small-caps text-slate-500">Coverage</div>
          <div className="mt-4 space-y-3">
            <div>
              <div className="small-caps text-slate-400">Loan Officer</div>
              <div className="font-semibold text-[17px] text-[#1F2937]">{app.loan_rep?.name || "Unassigned"}</div>
            </div>
            <div>
              <div className="small-caps text-slate-400">Advisor</div>
              <div className="font-semibold text-[17px] text-[#1F2937]">{app.broker?.name || "—"}</div>
            </div>
          </div>
          {isAdmin && (
            <div className="mt-6 border-t border-slate-200 pt-6">
              <div className="small-caps text-slate-500 mb-2">Assign loan officer</div>
              <Select value={app.loan_rep_id || ""} onValueChange={assignRep}>
                <SelectTrigger className="w-full bg-white" data-testid="assign-rep-select">
                  <SelectValue placeholder="Pick a loan officer" />
                </SelectTrigger>
                <SelectContent>
                  {team.filter((u) => u.role === "loan_rep" || u.role === "admin").map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="mt-2 text-xs text-slate-500">Assignment triggers a notification to the officer.</div>
              <div className="mt-6 flex flex-wrap gap-6">
                <button
                  onClick={async () => {
                    try {
                      const { data } = await api.post(`/applications/${app.id}/notify-team`);
                      if (data.delivered > 0) {
                        toast.success(`Email chain sent — ${data.delivered} email${data.delivered === 1 ? "" : "s"} delivered`);
                      } else {
                        toast.error(
                          `No emails sent. Check that a broker and loan officer are set on this file, and that SendGrid is configured.`,
                          { duration: 10000 },
                        );
                      }
                      load();
                    } catch (e) { toast.error(formatApiError(e)); }
                  }}
                  data-testid="start-email-chain-btn"
                  className="group inline-flex items-center gap-3"
                >
                  <span className="h-[1px] w-8 bg-slate-800 transition-all group-hover:w-12" />
                  <span className="small-caps text-[#1F2937]">Start email chain</span>
                </button>
                <button
                  onClick={async () => {
                    if (!confirm(`Duplicate this file? A new file will be created with the same details but no loan officer assigned, so you can pick a different one.`)) return;
                    try {
                      const { data } = await api.post(`/applications/${app.id}/duplicate`);
                      toast.success(`Duplicated as ${data.loan_number}`);
                      window.location.href = `/app/applications/${data.id}`;
                    } catch (e) { toast.error(formatApiError(e)); }
                  }}
                  data-testid="duplicate-file-btn"
                  className="group inline-flex items-center gap-3"
                >
                  <span className="h-[1px] w-8 bg-slate-800 transition-all group-hover:w-12" />
                  <span className="small-caps text-[#1F2937]">Duplicate file</span>
                </button>
              </div>
            </div>
          )}
          {canEdit && (
            <div className="mt-6 border-t border-slate-200 pt-6" data-testid="stage-update-block">
              <div className="small-caps text-slate-500 mb-2">Update Stage</div>
              <Select
                value={stageDraft?.stage || app.stage}
                onValueChange={openStageChange}
              >
                <SelectTrigger
                  className="border-0 shadow-none rounded-none px-0 bg-transparent focus:ring-0 font-semibold text-[16px] h-8"
                  data-testid="stage-update-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>{LOAN_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              {stageDraft && (
                <div className="mt-4 border border-slate-300 bg-slate-50 p-4" data-testid="stage-note-form">
                  <div className="small-caps text-slate-500 text-[10px]">
                    Moving to <span className="text-[#1F2937] font-semibold">{stageDraft.stage}</span>
                  </div>
                  <div className="mt-2 small-caps text-slate-500 mb-1">
                    Note to borrower <span className="text-slate-400">· optional</span>
                  </div>
                  <Textarea
                    rows={3}
                    autoFocus
                    value={stageDraft.note}
                    onChange={(e) => setStageDraft({ ...stageDraft, note: e.target.value })}
                    placeholder="What does the borrower need to do next? e.g., Please upload YTD P&L and last 3 months of bank statements."
                    data-testid="stage-note-textarea"
                    className="border-0 border-b border-slate-300 rounded-none px-0 bg-white shadow-none text-[14px] focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#1F2937] resize-none"
                  />
                  <div className="mt-3 flex items-center justify-between">
                    <div className="small-caps text-slate-500 text-[10px]">Sent as an alert + shown at the top of their file</div>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={cancelStageChange}
                        className="small-caps text-slate-500 hover:text-[#1F2937]"
                        data-testid="stage-note-cancel"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={commitStageChange}
                        className="small-caps text-[#1F2937] hover:opacity-70"
                        data-testid="stage-note-save"
                      >
                        Save →
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      <Tabs defaultValue="documents">
        <TabsList data-testid="app-tabs" className="bg-transparent p-0 gap-8 h-auto border-0">
          {[
            ["documents", "Documents"], ["notes", "Notes"],
            ["meetings", "Meetings"],
            ["timeline", "Timeline"],
          ].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}
              className="small-caps text-slate-500 data-[state=active]:text-[#1F2937] data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none px-0 py-2 border-b-2 border-transparent data-[state=active]:border-[#1F2937]">
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="documents" className="space-y-8 mt-8">
          <RequiredDocsChecklist app={app} docs={docs} canEdit={canEdit} onSaved={load} />
          <DocUpload appId={id} onUploaded={load} requiredDocs={app.required_documents} canRequestSignature={canEdit} />
          <div>
            <div className="small-caps text-slate-500 mb-4">Uploaded ({docs.length})</div>
            <div className="grid grid-cols-12 py-3 border-b border-slate-200 small-caps text-slate-500">
              <div className="col-span-4">File</div>
              <div className="col-span-2">Folder</div>
              <div className="col-span-2">Uploaded</div>
              <div className="col-span-2">By</div>
              <div className="col-span-2 text-right">Status</div>
            </div>
            {docs.map((d) => (
              <div key={d.id} className="grid grid-cols-12 items-center py-4 border-b border-slate-200 gap-x-4" data-testid={`doc-row-${d.id}`}>
                <div className="col-span-4 flex items-start gap-3 min-w-0 pr-4">
                  <FileText className="h-4 w-4 text-slate-400 shrink-0 mt-[3px]" />
                  <div className="min-w-0 flex-1">
                    <button
                      onClick={() => setPreviewDoc(d)}
                      className="block w-full truncate font-semibold text-[15px] text-[#1F2937] text-left hover:underline underline-offset-4"
                      data-testid={`doc-open-${d.id}`}
                      title={d.filename}
                    >
                      {prettyFilename(d.filename)}
                    </button>
                    <div className="small-caps text-slate-400 flex items-center gap-2 flex-wrap mt-0.5">
                      <span>{d.document_type}</span>
                      {d.source === "qualifier" && (
                        <span className="border border-slate-300 text-slate-500 px-1.5 py-0.5 text-[9px]">Qualifier</span>
                      )}
                      {d.source === "crm_webhook" && (
                        <span className="border border-slate-400 text-slate-600 px-1.5 py-0.5 text-[9px]">CRM</span>
                      )}
                      {d.requires_signature && (
                        <span
                          className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[9px] ${
                            d.signed ? "border-slate-800 text-[#1F2937]" : "border-slate-400 text-slate-600"
                          }`}
                          data-testid={`doc-sig-badge-${d.id}`}
                        >
                          {d.signed ? "Signed" : "Signature required"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-span-2 small-caps text-slate-500 truncate">{d.folder || d.document_type || "—"}</div>
                <div className="col-span-2 small-caps text-slate-500 truncate">{fmtDateTime(d.uploaded_at)}</div>
                <div className="col-span-2 small-caps text-slate-500 truncate">{d.uploaded_by_name || "—"}</div>
                <div className="col-span-2 flex items-center justify-end gap-3">
                  {d.requires_signature && !d.signed && user?.role === "borrower" && (
                    <button
                      onClick={() => setSigning({ doc: d, name: user?.name || "" })}
                      className="small-caps text-[#1F2937] underline underline-offset-4"
                      data-testid={`doc-sign-btn-${d.id}`}
                    >
                      Sign
                    </button>
                  )}
                  <DocStatusBadge status={d.status} canEdit={canEdit} onChange={(s) => setDocStatus(d.id, s)} />
                  <button
                    onClick={() => setPreviewDoc(d)}
                    className="text-slate-400 hover:text-[#1F2937]"
                    data-testid={`doc-preview-${d.id}`}
                    title="Preview (scroll through)"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => openAuthorizedDocument(`/documents/${d.id}/download`, d.filename).catch((e) => toast.error(e.message || "Could not open document"))}
                    className="text-slate-400 hover:text-[#1F2937]"
                    data-testid={`doc-download-${d.id}`}
                    title="Download"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button onClick={() => deleteDoc(d.id)} className="text-slate-400 hover:text-[#1F2937]" data-testid={`doc-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
            {docs.length === 0 && <div className="py-16 text-center font-semibold text-[16px] text-slate-400">No documents deposited yet.</div>}
          </div>
        </TabsContent>

        <TabsContent value="notes" className="mt-8 space-y-6">
          <NotesPanel appId={id} />
        </TabsContent>

        <TabsContent value="meetings" className="mt-8">
          <MeetingsPanel app={app} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="timeline" className="mt-8">
          <div className="small-caps text-slate-500 mb-6">Chronology</div>
          <ol className="relative border-l border-slate-200 ml-3 space-y-8">
            {(app.timeline || []).slice().reverse().map((t, i) => (
              <li key={i} className="ml-6" data-testid={`timeline-${i}`}>
                <span className="absolute -left-[5px] h-2.5 w-2.5 rounded-full bg-slate-800 mt-1.5" />
                <div className="font-semibold text-[18px] text-[#1F2937]">{t.stage}</div>
                <div className="small-caps text-slate-500 mt-1">{fmtDateTime(t.at)}</div>
                {t.note && <div className="mt-2 text-[14px] text-slate-600">{t.note}</div>}
              </li>
            ))}
          </ol>
        </TabsContent>

        <TabsContent value="ai-removed" className="mt-8 hidden" />
      </Tabs>

      {signing && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
          onClick={() => setSigning(null)}
          data-testid="sign-modal"
        >
          <div
            className="w-full max-w-lg bg-white border border-slate-300 p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="small-caps text-slate-500">Electronic Signature</div>
            <h3 className="mt-2 font-semibold text-[24px] text-[#1F2937]">Sign document</h3>
            <div className="mt-2 text-[14px] text-slate-600">
              {signing.doc.filename}
            </div>
            <button
              onClick={() => setPreviewDoc(signing.doc)}
              className="mt-3 inline-flex items-center gap-2 small-caps text-[#1F2937] underline underline-offset-4"
              data-testid="sign-preview-link"
            >
              <Eye className="h-3.5 w-3.5" /> Preview document
            </button>
            <div className="mt-6">
              <div className="small-caps text-slate-500 mb-1">Type your full legal name</div>
              <Input
                value={signing.name}
                onChange={(e) => setSigning({ ...signing, name: e.target.value })}
                data-testid="sign-name-input"
                className="border-0 border-b border-slate-400 rounded-none px-0 bg-transparent shadow-none font-semibold text-[22px] italic focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#1F2937]"
                placeholder="Your full legal name"
              />
              <div className="mt-2 text-[11px] text-slate-500">
                By typing your name, you agree this is your legally binding electronic signature.
              </div>
            </div>
            <div className="mt-8 flex items-center justify-end gap-6">
              <button
                onClick={() => setSigning(null)}
                className="small-caps text-slate-500 hover:text-[#1F2937]"
                data-testid="sign-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={submitSignature}
                disabled={!signing.name?.trim()}
                className="small-caps text-[#1F2937] disabled:opacity-40 inline-flex items-center gap-2"
                data-testid="sign-submit-btn"
              >
                <Check className="h-4 w-4" /> Sign and submit
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
          onClick={() => setConfirmDelete(false)}
          data-testid="file-delete-modal"
        >
          <div className="w-full max-w-md bg-white border border-slate-300 p-8" onClick={(e) => e.stopPropagation()}>
            <div className="small-caps text-red-700">Permanent deletion</div>
            <h3 className="mt-2 font-semibold text-[22px] text-[#1F2937]">Delete {app.loan_number}?</h3>
            <p className="mt-3 text-[14px] text-slate-600 leading-relaxed">
              This will permanently remove this file, all uploaded documents, messages, and tasks.
              This cannot be undone. Consider archiving instead.
            </p>
            <div className="mt-8 flex items-center justify-end gap-6">
              <button
                onClick={() => setConfirmDelete(false)}
                className="small-caps text-slate-500 hover:text-[#1F2937]"
                data-testid="file-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={deleteFile}
                className="small-caps text-red-700 inline-flex items-center gap-2"
                data-testid="file-delete-confirm"
              >
                <Trash2 className="h-4 w-4" /> Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}

      <DocumentPreviewModal
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        path={previewDoc ? `/documents/${previewDoc.id}/download` : null}
        filename={previewDoc?.filename}
        mimeType={previewDoc?.mime_type}
      />

      <TextBorrowerModal
        open={textOpen}
        onClose={() => setTextOpen(false)}
        app={app}
      />
    </div>
  );
}
